import { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import ReactApexChart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'
import { addDays } from '../../../components/novarium/periodRange'
import { OperatingRoomEvolutionPointDTO } from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { useChartTokens } from '../../ServicesStatistics/chartTokens'
import { PeakAnalysis } from '../../ServicesStatistics/peaks'
import { escapeHtml, parseIsoDate } from '../../ServicesStatistics/statisticsUtils'
import { axisLabel } from '../../ServicesStatistics/TimelineChart'
import {
  isLowVolume,
  isOpenPoint,
  OperatingRoomMeasure,
  pointTitleOf,
  pointValue,
} from '../operatingRoomEvolutionModel'
import { formatCount, formatHours, formatHoursPlain, formatPercent } from '../operatingRoomFormat'

interface OperatingRoomEvolutionChartProps {
  points: OperatingRoomEvolutionPointDTO[]
  granularity: 'DAY' | 'WEEK'
  /** «Hoy» del servidor: los puntos que lo incluyen no están cerrados. */
  asOf: string
  measure: OperatingRoomMeasure
  /** Nombre accesible del gráfico: la medida y el período («Evolución diaria · Cirugías»). */
  ariaLabel: string
  /** Promedio del período para la línea de referencia; null: sin línea. */
  average: number | null
  /** Picos de la ocupación, uno por posición de `points`; null con «Cirugías» y «Horas» (no hay picos). */
  analysis: PeakAnalysis | null
  /** Click, tap o Enter sobre un punto: abre su panel. `opener` es a donde vuelve el foco al cerrarlo. */
  onOpen: (point: OperatingRoomEvolutionPointDTO, opener: HTMLElement | null) => void
}

const CHART_HEIGHT = 280
const HALF_DAY_MS = 12 * 60 * 60 * 1000
/** Marcador de un punto común, de un pico y del punto resaltado con el teclado. */
const MARKER_SIZE = 3
const PEAK_MARKER_SIZE = 7
const ACTIVE_MARKER_SIZE = 8
/** ApexCharts avisa del click por dos eventos (`markerClick` y `dataPointSelection`): el segundo, dentro de este tiempo, se ignora. */
const DUPLICATE_CLICK_MS = 400
/** La ocupación nunca pasa de 100 %: el eje llega a 100 con una marca cada 20 puntos. */
const OCCUPANCY_AXIS_MAX = 100
const OCCUPANCY_AXIS_STEP = 20

/** Un punto del eje de tiempo: con `point` es un período dibujado; sin él, un extremo del rango que no tiene valor. */
interface PlotPoint {
  x: number
  y: number | null
  point: OperatingRoomEvolutionPointDTO | null
  /** Posición del punto en `points` (y en `analysis`). */
  index: number
}

const pointStart = (point: OperatingRoomEvolutionPointDTO): Date => parseIsoDate(point.date)

const SERIES_NAME_ID: Record<OperatingRoomMeasure, string> = {
  surgeries: 'OperatingRoomIndicatorsKpiSurgeries',
  hours: 'OperatingRoomIndicatorsKpiHours',
  occupancy: 'OperatingRoomIndicatorsKpiOccupancy',
}

interface PointFlags {
  peak: boolean
  lowVolume: boolean
  open: boolean
}

/** El valor de la medida escrito: «72,3 %», «21,5 h» o «14». */
function valueText(intl: IntlShape, point: OperatingRoomEvolutionPointDTO, measure: OperatingRoomMeasure): string {
  const value = pointValue(point, measure)
  if (value === null) return '—'
  if (measure === 'occupancy') return formatPercent(intl, value)
  return measure === 'hours' ? formatHours(intl, value) : formatCount(intl, value)
}

/** Tooltip del punto: su título, el valor de la medida con su desglose y las notas. */
function tooltipHtml(
  intl: IntlShape,
  point: OperatingRoomEvolutionPointDTO,
  granularity: 'DAY' | 'WEEK',
  measure: OperatingRoomMeasure,
  flags: PointFlags,
): string {
  const text = (id: string, values?: Record<string, string | number>) => escapeHtml(intl.formatMessage({ id }, values))
  const lines = [`<div class="nv-or-tip__title">${escapeHtml(pointTitleOf(intl, point, granularity))}</div>`]
  lines.push(`<div>${text(SERIES_NAME_ID[measure])}: ${escapeHtml(valueText(intl, point, measure))}</div>`)
  if (measure === 'surgeries') {
    lines.push(`<div>${text('OperatingRoomIndicatorsKpiUrgent')}: ${escapeHtml(formatCount(intl, point.urgent))}</div>`)
    lines.push(`<div>${text('OperatingRoomIndicatorsKpiScheduled')}: ${escapeHtml(formatCount(intl, point.scheduled))}</div>`)
    lines.push(`<div>${text('OperatingRoomIndicatorsKpiOutOfAgenda')}: ${escapeHtml(formatCount(intl, point.outOfAgenda))}</div>`)
  } else if (measure === 'hours') {
    lines.push(`<div>${text('OperatingRoomIndicatorsInsideBlocks')}: ${escapeHtml(formatHours(intl, point.insideBlockHours))}</div>`)
    lines.push(`<div>${text('OperatingRoomIndicatorsOutsideBlocks')}: ${escapeHtml(formatHours(intl, Math.max(0, point.hoursUsed - point.insideBlockHours)))}</div>`)
  } else {
    lines.push(`<div>${text('OperatingRoomIndicatorsOfHours', {
      count: formatHoursPlain(intl, point.insideBlockHours), total: formatHoursPlain(intl, point.capacityHours),
    })}</div>`)
  }
  if (flags.peak) lines.push(`<div class="nv-or-tip__note nv-or-tip__note--peak">${text('OperatingRoomIndicatorsTipPeak')}</div>`)
  if (flags.open) lines.push(`<div class="nv-or-tip__note">${text('OperatingRoomIndicatorsTipIncomplete')}</div>`)
  else if (flags.lowVolume) lines.push(`<div class="nv-or-tip__note">${text('OperatingRoomIndicatorsTipLow')}</div>`)
  lines.push(`<div class="nv-or-tip__note">${text('OperatingRoomIndicatorsTipOpen')}</div>`)
  return `<div class="nv-or-tip">${lines.join('')}</div>`
}

/**
 * Evolución: una línea con un punto por día (o por semana), de las cirugías, las horas o la ocupación. El eje X es un eje de
 * tiempo real: ApexCharts ralea solo los rótulos para que no se pisen. Una línea punteada marca el promedio del período (la
 * ocupación del indicador, que es ponderada, o las cirugías / horas por día o por semana). Un punto sin cerrar (el día en curso)
 * y, con la ocupación, uno de poca agenda llevan un marcador hueco (el % puede no ser representativo); los picos, un marcador más
 * grande con su etiqueta. Un punto sin valor (ocupación sin agenda) no se dibuja, no se alcanza con el teclado y no abre panel:
 * no cuenta como 0 %. Un click, un tap o Enter sobre un punto abre su panel; con el teclado, el contenedor es un grupo
 * enfocable: flechas e Inicio/Fin mueven un punto resaltado y una región `aria-live` dice dónde está (el mismo patrón que la
 * evolución de Indicadores de internación y de Atenciones de urgencia). El aviso del teclado es la descripción accesible del
 * grupo.
 * TODO(design-system): gráficos. Novarium no define el estilo de los gráficos.
 * TODO(design-system): color de pico en gráficos. Se usa el par --warn / --warn-bg de la etiqueta «Atención» del ranking.
 */
export default function OperatingRoomEvolutionChart({
  points, granularity, asOf, measure, ariaLabel, average, analysis, onOpen,
}: OperatingRoomEvolutionChartProps) {
  const intl = useIntl()
  const ref = useRef<HTMLDivElement>(null)
  const hintId = useId()
  const tokens = useChartTokens(ref)
  const isOccupancy = measure === 'occupancy'
  /** Posición en `plot` del punto resaltado con el teclado (apuntarlo con el mouse lo resuelve el tooltip). */
  const [activePlot, setActivePlot] = useState<number | null>(null)
  const lastOpenRef = useRef(0)

  /**
   * Lo que se dibuja: los puntos con valor. Se agregan puntos sin valor en los extremos del rango, para que el eje de tiempo
   * abarque todo el rango y ApexCharts no lo achique a los puntos dibujados (con un solo punto no sabe dibujar los rótulos del
   * eje); un rango de un solo punto lleva un día más a cada lado.
   */
  const plot = useMemo<PlotPoint[]>(() => {
    if (points.length === 0) return []
    const drawn: PlotPoint[] = points.flatMap((point, index) => {
      const y = pointValue(point, measure)
      return y !== null ? [{ x: pointStart(point).getTime(), y, point, index }] : []
    })
    const spare = points.length === 1 ? 1 : 0
    const lowX = addDays(pointStart(points[0]), -spare).getTime()
    const highX = addDays(pointStart(points[points.length - 1]), spare).getTime()
    if (drawn.length === 0 || drawn[0].x > lowX) drawn.unshift({ x: lowX, y: null, point: null, index: -1 })
    if (drawn[drawn.length - 1].x < highX) drawn.push({ x: highX, y: null, point: null, index: -1 })
    return drawn
  }, [points, measure])

  const series = useMemo(() => [{
    name: intl.formatMessage({ id: SERIES_NAME_ID[measure] }),
    data: plot.map(({ x, y }) => ({ x, y })),
  }], [intl, plot, measure])

  const flagsOf = useCallback((index: number): PointFlags => ({
    peak: analysis?.peaks.includes(index) ?? false,
    lowVolume: isLowVolume(points[index], measure, asOf),
    open: isOpenPoint(points[index], asOf),
  }), [analysis, points, measure, asOf])

  /** Los puntos a los que se llega con las flechas: los dibujados. */
  const navigable = useMemo(() => plot.flatMap((item, position) => (item.point ? [position] : [])), [plot])

  useEffect(() => { setActivePlot(null) }, [plot])

  /** Abre el panel del punto en la posición `position` de `plot`. */
  const openAt = useCallback((position: number) => {
    const point = plot[position]?.point
    if (!point) return
    const now = Date.now()
    if (now - lastOpenRef.current < DUPLICATE_CLICK_MS) return
    lastOpenRef.current = now
    setActivePlot(position)
    onOpen(point, ref.current)
  }, [plot, onOpen])

  const options = useMemo<ApexOptions | null>(() => {
    if (!tokens || plot.length === 0 || !ref.current) return null
    const rangeStart = pointStart(points[0]).getTime()
    const rangeEnd = pointStart(points[points.length - 1]).getTime()
    const warnBg = getComputedStyle(ref.current).getPropertyValue('--warn-bg').trim()
    const font = { fontFamily: tokens.font, fontSize: '12px' }

    // Marcadores huecos: puntos sin cerrar o de poca agenda
    const hollow = plot.flatMap((item, position) => (item.point && isLowVolume(item.point, measure, asOf)
      ? [{
        seriesIndex: 0, dataPointIndex: position, fillColor: tokens.card, strokeColor: tokens.primary,
        size: MARKER_SIZE, shape: 'circle' as const,
      }] : []))

    const peakAnnotations = plot.flatMap((item) => {
      if (!item.point || item.y === null || !analysis?.peaks.includes(item.index)) return []
      return [{
        x: item.x,
        y: item.y,
        marker: { size: PEAK_MARKER_SIZE, fillColor: tokens.warn, strokeColor: warnBg, strokeWidth: 2 },
        label: {
          text: `${axisLabel(intl, item.x, granularity)} · ${formatPercent(intl, item.y)}`,
          borderColor: tokens.warn,
          borderWidth: 1,
          offsetY: -4,
          style: { ...font, color: tokens.warn, background: warnBg, fontWeight: 600 },
        },
      }]
    })

    const activeItem = activePlot === null ? null : plot[activePlot]
    const activeAnnotations = activeItem && activeItem.point && activeItem.y !== null ? [{
      x: activeItem.x,
      y: activeItem.y,
      marker: { size: ACTIVE_MARKER_SIZE, fillColor: tokens.card, strokeColor: tokens.textStrong, strokeWidth: 2 },
    }] : []

    const unit = intl.formatMessage({ id: granularity === 'DAY' ? 'OperatingRoomIndicatorsPerDay' : 'OperatingRoomIndicatorsPerWeek' })
    const averageText = average === null ? '' : isOccupancy
      ? intl.formatMessage({ id: 'OperatingRoomIndicatorsAverageOccupancy' }, { value: formatPercent(intl, average) })
      : intl.formatMessage({ id: 'OperatingRoomIndicatorsAveragePer' }, {
        value: measure === 'hours' ? formatHours(intl, average) : intl.formatNumber(average, { maximumFractionDigits: 1, minimumFractionDigits: 1 }),
        unit,
      })

    return {
      chart: {
        type: 'line',
        height: CHART_HEIGHT,
        fontFamily: tokens.font,
        foreColor: tokens.text2,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        events: {
          // Con el tooltip compartido ApexCharts avisa del click con el punto más cercano, no hace falta acertarle
          markerClick: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
          dataPointSelection: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
        },
      },
      colors: [tokens.primary],
      states: { active: { filter: { type: 'none' } } },
      stroke: { width: 2, curve: 'straight', lineCap: 'round' },
      markers: {
        size: MARKER_SIZE,
        strokeWidth: 2,
        strokeColors: [tokens.primary],
        discrete: hollow,
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      grid: { borderColor: tokens.divider, strokeDashArray: 0 },
      annotations: {
        yaxis: average !== null ? [{
          y: average,
          borderColor: tokens.text2,
          strokeDashArray: 4,
          label: {
            text: averageText,
            position: 'left',
            textAnchor: 'start',
            offsetX: 4,
            borderWidth: 0,
            style: { ...font, color: tokens.text2, background: 'transparent' },
          },
        }] : [],
        points: [...peakAnnotations, ...activeAnnotations],
      },
      xaxis: {
        type: 'datetime',
        axisBorder: { color: tokens.divider },
        axisTicks: { color: tokens.divider },
        tooltip: { enabled: false },
        labels: {
          rotate: 0,
          datetimeUTC: false,
          hideOverlappingLabels: true,
          showDuplicates: false,
          style: { fontSize: '12px' },
          // Con un solo punto ApexCharts no pasa la marca de tiempo: el valor es la marca misma
          formatter: (value: string, timestamp?: number) => {
            const time = timestamp ?? Number(value)
            // Los días vacíos de los extremos (ver `plot`) no llevan rótulo: no son parte del rango
            if (!Number.isFinite(time) || time < rangeStart - HALF_DAY_MS || time > rangeEnd + HALF_DAY_MS) return ''
            return axisLabel(intl, time, granularity)
          },
        },
      },
      yaxis: {
        min: 0,
        ...(isOccupancy
          ? { max: OCCUPANCY_AXIS_MAX, tickAmount: OCCUPANCY_AXIS_MAX / OCCUPANCY_AXIS_STEP }
          : {
            forceNiceScale: true,
            title: {
              text: intl.formatMessage({ id: measure === 'hours' ? 'OperatingRoomIndicatorsAxisHours' : 'OperatingRoomIndicatorsAxisSurgeries' }),
              style: { fontSize: '12px', fontWeight: 500, color: tokens.text2 },
            },
          }),
        labels: {
          style: { fontSize: '12px' },
          formatter: (value: number) => (isOccupancy ? `${formatCount(intl, Math.round(value))} %` : formatCount(intl, Math.round(value))),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
          // Un punto sin valor es un extremo del rango: se muestra el punto dibujado más cercano
          const item = plot[dataPointIndex]?.point
            ? plot[dataPointIndex]
            : (dataPointIndex <= 0 ? plot.find((candidate) => candidate.point) : [...plot].reverse().find((candidate) => candidate.point))
          return item?.point ? tooltipHtml(intl, item.point, granularity, measure, flagsOf(item.index)) : ''
        },
      },
    }
  }, [intl, tokens, plot, points, granularity, asOf, isOccupancy, measure, average, analysis, activePlot, openAt, flagsOf])

  // ── Teclado ──

  /** Lo que se dice (y se ve) de un punto: «Sábado 19/09/2026: ocupación 72,3 %, 14,0 de 24,0 h. Pico.» */
  const describe = (position: number): string => {
    const item = plot[position]
    if (!item?.point) return ''
    const point = item.point
    const period = pointTitleOf(intl, point, granularity)
    const flags = flagsOf(item.index)
    const main = measure === 'occupancy'
      ? intl.formatMessage({ id: 'OperatingRoomIndicatorsLiveOccupancy' }, {
        period,
        value: valueText(intl, point, measure),
        count: formatHoursPlain(intl, point.insideBlockHours),
        total: formatHoursPlain(intl, point.capacityHours),
      })
      : measure === 'hours'
        ? intl.formatMessage({ id: 'OperatingRoomIndicatorsLiveHours' }, { period, value: valueText(intl, point, measure) })
        : intl.formatMessage({ id: 'OperatingRoomIndicatorsLiveSurgeries' }, {
          period, count: point.surgeries, countText: valueText(intl, point, measure),
        })
    return [
      main,
      flags.peak ? intl.formatMessage({ id: 'OperatingRoomIndicatorsLivePeak' }) : '',
      flags.open ? intl.formatMessage({ id: 'OperatingRoomIndicatorsLiveIncomplete' }) : flags.lowVolume ? intl.formatMessage({ id: 'OperatingRoomIndicatorsLiveLow' }) : '',
    ].filter(Boolean).join(' ')
  }

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    if (navigable.length === 0) return
    const current = activePlot === null ? -1 : navigable.indexOf(activePlot)
    let next: number | null = null
    if (event.key === 'ArrowRight') next = Math.min(navigable.length - 1, current + 1)
    else if (event.key === 'ArrowLeft') next = current < 0 ? navigable.length - 1 : Math.max(0, current - 1)
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = navigable.length - 1
    else if ((event.key === 'Enter' || event.key === ' ') && activePlot !== null) {
      event.preventDefault()
      openAt(activePlot)
      return
    }
    if (next !== null) {
      event.preventDefault()
      setActivePlot(navigable[next])
    }
  }

  const activeText = activePlot === null ? '' : describe(activePlot)

  return (
    <div className="nv-or-timeline">
      <div
        ref={ref}
        className="nv-or-chart"
        role="group"
        aria-label={ariaLabel}
        aria-roledescription={intl.formatMessage({ id: 'OperatingRoomIndicatorsChartRole' })}
        aria-describedby={hintId}
        tabIndex={0}
        onKeyDown={move}
        onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActivePlot(null) }}
      >
        <div aria-hidden="true">
          {options && (
            <ReactApexChart key={`${measure}-${granularity}`} type="line" height={CHART_HEIGHT} series={series} options={options} />
          )}
        </div>
        <span id={hintId} className="nv-sr-only">{intl.formatMessage({ id: 'OperatingRoomIndicatorsChartHint' })}</span>
        <span className="nv-sr-only" role="status" aria-live="polite">{activeText}</span>
      </div>
      {/* Solo el punto resaltado con el teclado; el lector de pantalla lo oye por la región `aria-live` de arriba */}
      {activeText && <p className="nv-or-timeline__detail nv-help" aria-hidden="true">{activeText}</p>}
    </div>
  )
}
